import { useQuery } from '@tanstack/react-query';
import { lineTotalCents, normalizePlu, parseScaleBarcode, type PosProductDTO, type PosStateDTO, type ScaleConfig } from '@bifeai/shared';
import { api } from './api';
import { withSnapshot } from './offline';

export function usePosState() {
  return useQuery({
    queryKey: ['pos', 'state'],
    queryFn: () => withSnapshot('pos-state', () => api.get<PosStateDTO>('/pos/state')),
  });
}

export function usePosCatalog(enabled: boolean) {
  return useQuery({
    queryKey: ['pos', 'catalog'],
    queryFn: () => withSnapshot('pos-catalog', () => api.get<PosProductDTO[]>('/pos/catalog')),
    enabled,
    staleTime: 60_000,
  });
}

/** Renglón del ticket en pantalla (todavía no cobrado). */
export interface CartLine {
  key: string;
  product: PosProductDTO;
  /** Gramos o unidades. */
  qty: number;
  unitPriceCents: number;
  lineTotalCents: number;
  /** Etiqueta escaneada: se manda a la API tal cual para que la vuelva a leer. */
  barcode: string | null;
  /** El peso lo leyó la balanza conectada a la computadora. */
  fromScale?: boolean;
}

let seq = 0;
export const lineKey = () => `l${Date.now().toString(36)}${(seq++).toString(36)}`;

export function makeLine(product: PosProductDTO, qty: number, barcode: string | null = null, labelTotalCents?: number): CartLine {
  const price = product.priceCents ?? 0;
  return {
    key: lineKey(),
    product,
    qty,
    unitPriceCents: price,
    lineTotalCents: labelTotalCents ?? lineTotalCents(product.unitType, qty, price),
    barcode,
  };
}

/** needsPrice: producto con precio al momento (ej.: grasa): hay que preguntar el precio antes de agregarlo. */
export type ScanOutcome = { ok: true; line: CartLine; needsPrice?: boolean } | { ok: false; message: string };

/**
 * Lee un código en el mostrador, al instante y sin internet: etiqueta de balanza (por PLU)
 * o código de barras común (por código interno). La API lo vuelve a validar al cobrar.
 */
export function scanLocally(code: string, catalog: PosProductDTO[], scale: ScaleConfig): ScanOutcome {
  const reading = parseScaleBarcode(code, scale);
  if (!reading.ok && reading.reason === 'BAD_CHECK_DIGIT') {
    return {
      ok: false,
      message: 'La etiqueta no se leyó bien. Volvé a escanearla.',
    };
  }
  if (reading.ok) {
    const matches = catalog.filter((p) => p.plu && normalizePlu(p.plu) === reading.plu);
    if (matches.length === 0) {
      return {
        ok: false,
        message: `No hay ningún producto con código de balanza ${reading.plu}. Cargalo en Productos (campo "Código de balanza").`,
      };
    }
    if (matches.length > 1)
      return {
        ok: false,
        message: `Hay ${matches.length} productos con el código de balanza ${reading.plu}.`,
      };
    const p = matches[0]!;
    if (p.openPrice && reading.grams !== null && p.unitType === 'WEIGHT') {
      return { ok: true, line: makeLine(p, reading.grams, code), needsPrice: true };
    }
    if (!p.priceCents) return { ok: false, message: `${p.name} no tiene precio cargado.` };
    if (reading.priceCents !== null) {
      const qty =
        p.unitType === 'WEIGHT' ? Math.round((reading.priceCents * 1000) / p.priceCents) : Math.max(1, Math.round(reading.priceCents / p.priceCents));
      return {
        ok: true,
        line: makeLine(p, Math.max(1, qty), code, reading.priceCents),
      };
    }
    if (p.unitType !== 'WEIGHT')
      return {
        ok: false,
        message: `${p.name} se vende por unidad, pero la etiqueta trae un peso.`,
      };
    return { ok: true, line: makeLine(p, reading.grams!, code) };
  }
  if (!reading.ok && reading.reason === 'BAD_VALUE') {
    return {
      ok: false,
      message: 'La etiqueta no trae peso ni importe. Revisá el formato de balanza en Configuración.',
    };
  }
  const bySku = catalog.find((p) => p.sku && p.sku.toLowerCase() === code.toLowerCase());
  if (bySku) {
    if (!bySku.priceCents) return { ok: false, message: `${bySku.name} no tiene precio cargado.` };
    if (bySku.unitType === 'WEIGHT')
      return {
        ok: false,
        message: `${bySku.name} se vende por kilo: cargá el peso.`,
      };
    return { ok: true, line: makeLine(bySku, 1, code) };
  }
  const byPlu = /^\d{1,6}$/.test(code) ? catalog.find((p) => p.plu && normalizePlu(p.plu) === normalizePlu(code)) : undefined;
  if (byPlu)
    return {
      ok: false,
      message: `PLU ${code}: ${byPlu.name}. Tocá el producto para cargar el peso.`,
    };
  return { ok: false, message: `No encontramos el código ${code}.` };
}

/** Identificador de la venta: se genera al empezar el ticket y se reutiliza si hay que reintentar el cobro. */
export function newClientId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // Navegadores viejos: UUID v4 con números aleatorios seguros.
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Redondeos sugeridos para "paga con": el total y los billetes que siguen. */
export function cashSuggestions(totalCents: number): number[] {
  const pesos = Math.ceil(totalCents / 100);
  const steps = [1_000, 2_000, 5_000, 10_000, 20_000];
  const out = new Set<number>([pesos]);
  for (const step of steps) out.add(Math.ceil(pesos / step) * step);
  return [...out]
    .filter((v) => v >= pesos)
    .sort((a, b) => a - b)
    .slice(0, 5)
    .map((v) => v * 100);
}
